import type { ColorProps } from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faBug } from '@fortawesome/pro-regular-svg-icons'
import {
  faEllipsis,
  faFaceFrownOpen,
  faFlag,
  faMask,
  faUserLock,
  faWarning,
} from '@fortawesome/pro-solid-svg-icons'
import { defaultTo, transform } from 'lodash-es'
import { Duration } from 'luxon'

import composeRichText from '@/client/services/richText/composeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'
import { SuspensionActionContext } from '@/client/services/user'

import type {
  DetectedIssueActionConfig,
  DetectedIssueCategory,
  DetectedIssueType,
  DetectedIssueTypeWithSuspensionExplanation,
  DetectedIssueUrgency,
  ModeratedEntityType,
  ModerationFlagCategory,
  ModerationFlagSeverity,
  ModerationFlagViolationType,
  ReportableDetectedIssueType,
  SelectableIssueCategory,
} from './types'

export const MODERATION_FLAG_CATEGORIES = [
  'sharing-personal-info',
  'leveraging-other-platform',
  'inappropriate-language',
  'inappropriate-image',
  'unknown',
] as const

export const MODERATION_FLAG_SEVERITIES = ['green', 'yellow', 'red'] as const

export const MODERATION_FLAG_VIOLATION_TYPES = ['overt', 'extreme', 'potential'] as const

export const MODERATION_FLAG_CATEGORY_TO_LABEL: Record<ModerationFlagCategory, string> = {
  'sharing-personal-info': 'Sharing Personal Info',
  'leveraging-other-platform': 'Leveraging Other Platform',
  'inappropriate-language': 'Inappropriate Langauge',
  'inappropriate-image': 'Inappropriate Image',
  unknown: 'Unknown',
}

export const MODERATION_FLAG_CATEGORY_TO_BLOCKED_CONTENT_ISSUE_DESCRIPTION: Record<
  ModerationFlagCategory,
  string | null
> = {
  'sharing-personal-info': 'sharing personal contact information or social media handles',
  'leveraging-other-platform': 'sharing direct Zoom or video meeting links',
  'inappropriate-language': 'language that isn’t classroom appropriate',
  'inappropriate-image': `images that aren't classroom appropriate`,
  unknown: null,
}

export const DEFAULT_CONTENT_FILTER_MODERATION_FLAG_SEVERITY: ModerationFlagSeverity = 'yellow'

export const MODERATION_FLAG_SEVERITY_TO_LABEL: Record<ModerationFlagSeverity, string> = {
  red: 'Red',
  yellow: 'Yellow',
  green: 'Green',
}

export const MODERATION_FLAG_VIOLATION_TYPE_TO_LABEL: Record<ModerationFlagViolationType, string> =
  {
    overt: 'Overt Violation',
    extreme: 'Extreme Violation',
    potential: 'Potential Violation',
  }

export const MODERATION_FLAG_SEVERITY_TO_UI_DETAILS: Record<
  ModerationFlagSeverity,
  {
    inactiveBgColor: ColorProps['color']
    inactiveBorderColor: ColorProps['color']
    inactiveHoverBgColor: ColorProps['color']
    activeBgColor: ColorProps['color']
    activeBorderColor: ColorProps['color']
    activeHoverBgColor: ColorProps['color']
  }
> = {
  red: {
    inactiveBgColor: 'bg',
    inactiveHoverBgColor: 'red.subtle',
    inactiveBorderColor: 'red.muted',
    activeBgColor: 'red.focusRing',
    activeBorderColor: 'red.focusRing',
    activeHoverBgColor: 'red.solid',
  },
  green: {
    inactiveBgColor: 'bg',
    inactiveHoverBgColor: 'green.subtle',
    inactiveBorderColor: 'green.muted',
    activeBgColor: 'green.focusRing',
    activeBorderColor: 'green.focusRing',
    activeHoverBgColor: 'green.solid',
  },
  yellow: {
    inactiveBgColor: 'bg',
    inactiveHoverBgColor: 'yellow.subtle',
    inactiveBorderColor: 'yellow.muted',
    activeBgColor: 'yellow.solid',
    activeBorderColor: 'yellow.solid',
    activeHoverBgColor: 'yellow.focusRing',
  },
}
export const DETECTED_ISSUE_CATEGORIES = [
  'safety-or-wellbeing-concerns',
  'dishonesty',
  'crossing-privacy-boundaries',
  'made-me-uncomfortable',
  'content-violates-guidelines',
  'other',
] as const

export const SELECTABLE_ISSUE_CATEGORIES: SelectableIssueCategory[] = [
  'technical-issue',
  ...DETECTED_ISSUE_CATEGORIES,
]

export const SELECTABLE_ISSUE_CATEGORY_TO_DISPLAY_INFO: Record<
  SelectableIssueCategory,
  { label: string; icon: IconDefinition }
> = {
  'safety-or-wellbeing-concerns': {
    label: 'Safety or wellbeing concerns',
    icon: faWarning,
  },
  dishonesty: {
    label: 'Dishonesty',
    icon: faMask,
  },
  'crossing-privacy-boundaries': {
    label: 'Asking for personal information or crossing privacy boundaries',
    icon: faUserLock,
  },
  'made-me-uncomfortable': {
    label: 'Behavior that made me uncomfortable or was inappropriate',
    icon: faFaceFrownOpen,
  },
  'content-violates-guidelines': {
    label: "Session or post content didn't follow guidelines",
    icon: faFlag,
  },
  'technical-issue': {
    label: 'Technical issue',
    icon: faBug,
  },
  other: {
    label: 'Something else',
    icon: faEllipsis,
  },
}

const NON_REPORTABLE_DETECTED_ISSUE_TYPES = [
  'tutor-no-show',
  'learner-no-show',
  'tutor-removed-learner',
  'vague-live-help-request',
  'age-assurance-failed',
  'user-under-13-self-report',
  'background-check-suspended',
  'background-check-failed',
  'benign',
  'duplicate',

  // Historical
  'not-let-into-session',
  'circumventing-post-limits',
  'classroom-inappropriate-behavior',
] as const

export const REPORTABLE_DETECTED_ISSUE_TYPES = [
  'disrespectful-behavior',
  'explicit-language',
  'suspicious-interactions-with-minor',
  'severely-crude-or-inappropriate-behavior',
  'threatening-violence',
  'repetitive-or-frivolous-communication',
  'promotional-content',
  'irrelevant-content',
  'content-in-wrong-place',
  'gaming-sp-or-volunteer-hours',
  'circumventing-safety-features',
  'user-under-13-peer-report',
  'poor-help-quality',
  'inaccurate-help',
  'sharing-personal-information',
  'requesting-personal-information',
  'overly-personal-content',
  'impersonation-or-misrepresentation',
  'suicide-or-self-harm',
  'slurs-and-hate-speech',
  'romantic-or-sexual-advances',
  'political-religious-advocacy',
  'non-english-session',
  'cheating',
  'disintermediation',
  'suspension-evasion',
  'off-topic-conversation',
  'session-guidelines-violation',
  'harmful-generalizations',

  // Generic types

  'safety-or-wellbeing-concerns-other',
  'dishonesty-other',
  'crossing-privacy-boundaries-other',
  'made-me-uncomfortable-other',
  'content-violates-guidelines-other',

  'other',
] as const

export const DETECTED_ISSUE_TYPES = [
  ...REPORTABLE_DETECTED_ISSUE_TYPES,
  ...NON_REPORTABLE_DETECTED_ISSUE_TYPES,
] as const

export const DETECTED_ISSUE_TYPES_WITH_SUSPENSION_EXPLANATION = [
  'suspicious-interactions-with-minor',
  'severely-crude-or-inappropriate-behavior',
  'threatening-violence',
  'slurs-and-hate-speech',
  'tutor-removed-learner',
  'overly-personal-content',
  'suspension-evasion',
  'circumventing-safety-features',
  'vague-live-help-request',
  'user-under-13-self-report',
  'background-check-failed',
] as const satisfies DetectedIssueType[]

export const DETECTED_ISSUE_TYPES_BY_CATEGORY: Record<
  DetectedIssueCategory,
  ReportableDetectedIssueType[]
> = {
  'safety-or-wellbeing-concerns': [
    'suicide-or-self-harm',
    'severely-crude-or-inappropriate-behavior',
    'threatening-violence',
    'suspicious-interactions-with-minor',
    'slurs-and-hate-speech',
    'impersonation-or-misrepresentation',
    'harmful-generalizations',
    'user-under-13-peer-report',
    'safety-or-wellbeing-concerns-other',
  ],
  'made-me-uncomfortable': [
    'slurs-and-hate-speech',
    'off-topic-conversation',
    'disintermediation',
    'impersonation-or-misrepresentation',
    'disrespectful-behavior',
    'circumventing-safety-features',
    'explicit-language',
    'romantic-or-sexual-advances',
    'political-religious-advocacy',
    'harmful-generalizations',
    'made-me-uncomfortable-other',
  ],
  'crossing-privacy-boundaries': [
    'overly-personal-content',
    'disintermediation',
    'requesting-personal-information',
    'sharing-personal-information',
    'romantic-or-sexual-advances',
    'crossing-privacy-boundaries-other',
  ],
  'content-violates-guidelines': [
    'content-in-wrong-place',
    'irrelevant-content',
    'session-guidelines-violation',
    'off-topic-conversation',
    'promotional-content',
    'poor-help-quality',
    'inaccurate-help',
    'non-english-session',
    'repetitive-or-frivolous-communication',
    'content-violates-guidelines-other',
  ],
  dishonesty: [
    'suspension-evasion',
    'impersonation-or-misrepresentation',
    'circumventing-safety-features',
    'disintermediation',
    'user-under-13-peer-report',
    'gaming-sp-or-volunteer-hours',
    'cheating',
    'dishonesty-other',
  ],
  other: ['other'],
}

export const DETECTED_ISSUE_TYPE_TO_CATEGORIES: Record<
  ReportableDetectedIssueType,
  DetectedIssueCategory[]
> = transform(
  DETECTED_ISSUE_TYPES_BY_CATEGORY,
  (result, types, category: DetectedIssueCategory) => {
    types.forEach(type => {
      const categories = defaultTo(result[type], []) satisfies DetectedIssueCategory[]
      categories.push(category)
      result[type] = categories
    })
  },
  // eslint-disable-next-line local/no-new-type-assertions
  {} as Record<ReportableDetectedIssueType, DetectedIssueCategory[]>
)

export const DETECTED_ISSUE_TYPE_TO_ADMIN_LABEL: Record<DetectedIssueType, string> = {
  'tutor-no-show': 'Tutor no-show',
  'learner-no-show': 'Learner no-show',
  'tutor-removed-learner': 'Tutor removed learner',
  'vague-live-help-request': 'Vague live help request',
  'age-assurance-failed': 'Age assurance failed',
  'user-under-13-self-report': 'User under 13 self-report',
  'background-check-suspended': 'Background check suspended',
  'background-check-failed': 'Background check failed',

  'disrespectful-behavior': 'Disrespectful behavior',
  'explicit-language': 'Explicit language',
  'suspicious-interactions-with-minor': 'Suspicious interactions with minor',
  'severely-crude-or-inappropriate-behavior': 'Severely crude or inappropriate behavior',
  'threatening-violence': 'Threatening violence',
  'repetitive-or-frivolous-communication': 'Repetitive or frivolous communication',
  'promotional-content': 'Promotional content',
  'irrelevant-content': 'Irrelevant content',
  'content-in-wrong-place': 'Content in wrong place',
  'gaming-sp-or-volunteer-hours': 'Gaming SP or volunteer hours',
  'circumventing-safety-features': 'Circumventing safety features',
  'user-under-13-peer-report': 'User under 13 peer report',
  'poor-help-quality': 'Poor help quality',
  'inaccurate-help': 'Inaccurate help',
  'sharing-personal-information': 'Sharing personal information',
  'requesting-personal-information': 'Requesting personal information',
  'overly-personal-content': 'Overly personal content',
  'suicide-or-self-harm': 'Suicide or self-harm',
  'slurs-and-hate-speech': 'Slurs and hate speech',
  'romantic-or-sexual-advances': 'Romantic or sexual advances',
  'political-religious-advocacy': 'Political/religious advocacy',
  'non-english-session': 'Non-English session',
  cheating: 'Cheating',
  disintermediation: 'Disintermediation',
  'suspension-evasion': 'Suspension evasion',
  'impersonation-or-misrepresentation': 'Impersonation or misrepresentation',
  'off-topic-conversation': 'Off-topic conversation',
  'session-guidelines-violation': 'Session guidelines violation',
  'harmful-generalizations': 'Harmful generalizations',

  'safety-or-wellbeing-concerns-other': 'Safety or wellbeing concerns',
  'dishonesty-other': 'Dishonesty',
  'crossing-privacy-boundaries-other': 'Crossing privacy boundaries',
  'made-me-uncomfortable-other': 'Made me uncomfortable',
  'content-violates-guidelines-other': 'Content violates guidelines',

  benign: 'Benign',
  duplicate: 'Duplicate',

  other: 'Other',

  'not-let-into-session': 'Not let into session',
  'circumventing-post-limits': 'Circumventing post limits',
  'classroom-inappropriate-behavior': 'Classroom inappropriate behavior',
}

export const DETECTED_ISSUE_TYPE_TO_USER_FACING_LABEL: Record<DetectedIssueType, string> = {
  'tutor-no-show': 'Tutor no-show',
  'learner-no-show': 'Learner no-show',
  'tutor-removed-learner': 'Tutor removed learner',
  'vague-live-help-request': 'Vague live help request',
  'age-assurance-failed': 'Age assurance failed',
  'user-under-13-self-report': 'User under 13 self-report',
  'background-check-suspended': 'Background check suspended',
  'background-check-failed': 'Background check failed',

  'disrespectful-behavior': 'Someone was rude, disrespectful, or harsh',
  'explicit-language': 'Someone used explicit or inappropriate language casually',
  'suspicious-interactions-with-minor': 'Something felt unsafe involving a minor and an adult',
  'severely-crude-or-inappropriate-behavior':
    'Someone did or said something extremely inappropriate or vulgar',
  'threatening-violence': 'Someone talked about hurting someone or threatened violence',
  'repetitive-or-frivolous-communication': 'Someone posted repetitive or frivolous messages',
  'promotional-content': 'Someone promoted their own sessions or things outside of Schoolhouse',
  'irrelevant-content': "Someone shared content that wasn't relevant to the topic",
  'content-in-wrong-place': 'Someone posted something in the wrong place',
  'gaming-sp-or-volunteer-hours':
    'Someone tried to fake or boost their SP, certifications, or volunteer hours',
  'circumventing-safety-features': 'Someone tried to get around safety rules or restrictions',
  'user-under-13-peer-report': 'Someone might be under 13',
  'poor-help-quality': "Someone gave poor-quality help or didn't explain things clearly",
  'inaccurate-help': 'Someone shared help or answers that were inaccurate',
  'sharing-personal-information': "Someone shared their own contact info or someone else's",
  'requesting-personal-information':
    'Someone asked for my contact info (email, phone, social media, etc.)',
  'overly-personal-content': 'Someone asked me personal questions that felt too private',
  'suicide-or-self-harm': "I'm worried someone might hurt themselves",
  'slurs-and-hate-speech': 'Someone used extremely hateful or discriminatory language',
  'romantic-or-sexual-advances': 'Someone made romantic or sexual comments or advances',
  'political-religious-advocacy':
    'Someone pushed political or religious views in an inappropriate context',
  'non-english-session': 'Someone ran a session in a language other than English',
  cheating: 'Someone was cheating or trying to get answers without learning',
  disintermediation:
    'Someone tried to take tutoring or conversations off the platform in a sneaky way',
  'suspension-evasion': 'Someone appears to be trying to come back after being banned',
  'impersonation-or-misrepresentation': 'Someone pretended to be someone else',
  'off-topic-conversation': 'Someone’s off-topic discussion made it hard to stay focused',
  'session-guidelines-violation':
    'Someone presented material that wasn’t academic or in the session description',
  'harmful-generalizations':
    'Someone made stereotyping or biased assumptions about a group of people',

  // Generic types

  'safety-or-wellbeing-concerns-other': 'Something else serious happened',
  'dishonesty-other': 'Something else serious happened',
  'crossing-privacy-boundaries-other': 'Something else crossed my personal boundaries',
  'made-me-uncomfortable-other': 'Something else inappropriate happened',
  'content-violates-guidelines-other': 'Something else about the content didn’t follow guidelines',

  benign: 'Benign',
  duplicate: 'Duplicate',

  other: 'Something else happened that isn’t listed above',

  // Historical
  'not-let-into-session': `Tutor didn't let me into the session`,
  'circumventing-post-limits': 'Circumventing post limits',
  'classroom-inappropriate-behavior': 'Other classroom-inappropriate behavior',
}

export const USER_FACING_LABEL_OVERRIDES_BY_CATEGORY: Partial<
  Record<DetectedIssueCategory, Partial<Record<ReportableDetectedIssueType, string>>>
> = {
  'safety-or-wellbeing-concerns': {
    'impersonation-or-misrepresentation': "Someone pretended to be someone they're not",
  },
  'crossing-privacy-boundaries': {
    disintermediation: 'Someone tried to meet or offer tutoring off Schoolhouse',
    'romantic-or-sexual-advances':
      'Someone made romantic or sexual comments toward me or another user',
  },
  'made-me-uncomfortable': {
    'slurs-and-hate-speech': 'Someone used extremely hateful or offensive language',
    disintermediation: 'Someone tried to meet or offer tutoring off Schoolhouse',
    'impersonation-or-misrepresentation':
      "Someone impersonated another person or wasn't honest about who they were",
    'circumventing-safety-features': 'Someone tried to get around rules or safety features',
  },
}

export function getUserFacingLabel(
  issueType: ReportableDetectedIssueType,
  category?: DetectedIssueCategory | null
): string {
  if (category != null) {
    const override = USER_FACING_LABEL_OVERRIDES_BY_CATEGORY[category]?.[issueType]
    if (override != null) {
      return override
    }
  }
  return DETECTED_ISSUE_TYPE_TO_USER_FACING_LABEL[issueType]
}

export const DETECTED_ISSUE_TYPE_TO_URGENCY: Record<DetectedIssueType, DetectedIssueUrgency> = {
  'tutor-no-show': 'low',
  'learner-no-show': 'low',
  'tutor-removed-learner': 'low',
  'vague-live-help-request': 'low',
  'age-assurance-failed': 'low',
  'user-under-13-self-report': 'low',
  'background-check-suspended': 'low',
  'background-check-failed': 'high',

  'disrespectful-behavior': 'medium',
  'explicit-language': 'low',
  'suspicious-interactions-with-minor': 'high',
  'severely-crude-or-inappropriate-behavior': 'high',
  'threatening-violence': 'high',
  'repetitive-or-frivolous-communication': 'low',
  'promotional-content': 'low',
  'irrelevant-content': 'low',
  'content-in-wrong-place': 'low',
  'gaming-sp-or-volunteer-hours': 'low',
  'circumventing-safety-features': 'medium',
  'user-under-13-peer-report': 'low',
  'poor-help-quality': 'low',
  'inaccurate-help': 'low',
  'sharing-personal-information': 'low',
  'requesting-personal-information': 'low',
  'overly-personal-content': 'high',
  'suicide-or-self-harm': 'high',
  'slurs-and-hate-speech': 'high',
  'romantic-or-sexual-advances': 'medium',
  'political-religious-advocacy': 'medium',
  'non-english-session': 'medium',
  cheating: 'medium',
  disintermediation: 'medium',
  'suspension-evasion': 'high',
  'impersonation-or-misrepresentation': 'medium',
  'off-topic-conversation': 'medium',
  'session-guidelines-violation': 'medium',
  'harmful-generalizations': 'medium',

  // Generic types

  'safety-or-wellbeing-concerns-other': 'high',
  'dishonesty-other': 'low',
  'crossing-privacy-boundaries-other': 'low',
  'made-me-uncomfortable-other': 'low',
  'content-violates-guidelines-other': 'low',

  benign: 'low',
  duplicate: 'low',

  other: 'low',

  // Historical
  'not-let-into-session': 'low',
  'circumventing-post-limits': 'low',
  'classroom-inappropriate-behavior': 'low',
}

export const HIGH_URGENCY_DETECTED_ISSUE_TYPES = DETECTED_ISSUE_TYPES.filter(
  type => DETECTED_ISSUE_TYPE_TO_URGENCY[type] === 'high'
)

// IMPORTANT NOTE: When you add new types here, add a conversion in contentFlagToModerationEntity if applicable
export const MODERATED_ENTITY_TYPES = [
  'post',
  'message',
  'post-comment',
  'session-host',
  'series-host',
  'session-registration',
  'series-registration',
  'content-flag',
  'session-comment',
  'series-comment',
  'user',
] as const

export const MODERATED_ENTITY_TYPE_TO_LABEL: Record<ModeratedEntityType, string> = {
  post: 'post',
  message: 'message',
  'post-comment': 'comment',
  'session-host': 'session',
  'series-host': 'series',
  'session-registration': 'session',
  'series-registration': 'series',
  'content-flag': 'content flag',
  'session-comment': 'comment',
  'series-comment': 'comment',
  user: 'user',
}

export const DETECTED_ISSUE_ACTION_CONFIGS: Record<DetectedIssueType, DetectedIssueActionConfig> = {
  'tutor-no-show': {
    baseActions: [],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'tutor-no-show',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
      duration: Duration.fromObject({ days: 30 }),
    },
    penaltyActions: [
      {
        type: 'revoke-tutor-privilege',
      },
      {
        type: 'warning',
        issueType: 'tutor-no-show',
      },
    ],
    disableAutoVoidingStrikes: true,
  },
  'learner-no-show': {
    baseActions: [],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'learner-no-show',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
      duration: Duration.fromObject({ days: 30 }),
    },
    penaltyActions: [
      {
        type: 'handle-learner-no-show',
        suspensionActionContext: SuspensionActionContext.LearnerNoShows,
      },
    ],
  },
  'tutor-removed-learner': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'tutor-removed-learner',
      },
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'vague-live-help-request': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 3,
      duration: Duration.fromObject({ hour: 1 }),
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
    disableAutoVoidingStrikes: true,
  },
  'age-assurance-failed': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
    disableDisputes: true,
  },
  'user-under-13-self-report': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'background-check-suspended': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'background-check-failed': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  'disrespectful-behavior': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'reporter-message',
        issueType: 'disrespectful-behavior',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'explicit-language': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'reporter-message',
        issueType: 'explicit-language',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'explicit-language',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'explicit-language',
      },
    ],
  },
  'suspicious-interactions-with-minor': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'suspicious-interactions-with-minor',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'severely-crude-or-inappropriate-behavior': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'severely-crude-or-inappropriate-behavior',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'threatening-violence': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'threatening-violence',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'repetitive-or-frivolous-communication': {
    baseActions: [
      { type: 'hide-entity' },
      { type: 'reporter-message', issueType: 'repetitive-or-frivolous-communication' },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'repetitive-or-frivolous-communication',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'repetitive-or-frivolous-communication',
      },
    ],
  },
  'promotional-content': {
    baseActions: [
      { type: 'hide-entity' },
      { type: 'reporter-message', issueType: 'promotional-content' },
    ],
    strikeActions: [{ type: 'warning', issueType: 'promotional-content' }],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'promotional-content',
      },
    ],
  },
  'irrelevant-content': {
    baseActions: [
      { type: 'hide-entity' },
      { type: 'reporter-message', issueType: 'irrelevant-content' },
    ],
    strikeActions: [{ type: 'warning', issueType: 'irrelevant-content' }],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'irrelevant-content',
      },
    ],
  },
  'content-in-wrong-place': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'content-in-wrong-place',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'content-in-wrong-place',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },
  'gaming-sp-or-volunteer-hours': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'gaming-sp-or-volunteer-hours',
      },
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [],
  },
  'circumventing-safety-features': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'circumventing-safety-features',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'circumventing-safety-features',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
    ],
  },
  'user-under-13-peer-report': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'reporter-message',
        issueType: 'user-under-13-peer-report',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'poor-help-quality': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'poor-help-quality',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'poor-help-quality',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },
  'inaccurate-help': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'inaccurate-help',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'inaccurate-help',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },
  'sharing-personal-information': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'reporter-message',
        issueType: 'sharing-personal-information',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'sharing-personal-information',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'revoke-tutor-privilege',
      },
      {
        type: 'warning',
        issueType: 'sharing-personal-information',
      },
    ],
  },
  'requesting-personal-information': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'reporter-message',
        issueType: 'requesting-personal-information',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'requesting-personal-information',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'revoke-tutor-privilege',
      },
      {
        type: 'warning',
        issueType: 'requesting-personal-information',
      },
    ],
  },
  'overly-personal-content': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'overly-personal-content',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'suicide-or-self-harm': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'suicide-or-self-harm',
      },
      {
        type: 'reporter-message',
        issueType: 'suicide-or-self-harm',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
    disableDisputes: true,
  },
  'slurs-and-hate-speech': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'slurs-and-hate-speech',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'romantic-or-sexual-advances': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'reporter-message',
        issueType: 'romantic-or-sexual-advances',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'romantic-or-sexual-advances',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'romantic-or-sexual-advances',
      },
    ],
  },
  'political-religious-advocacy': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'reporter-message',
        issueType: 'political-religious-advocacy',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'political-religious-advocacy',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'warning',
        issueType: 'political-religious-advocacy',
      },
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },
  'non-english-session': {
    baseActions: [
      {
        type: 'hide-entity',
      },
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'non-english-session',
      },
      {
        type: 'reporter-message',
        issueType: 'non-english-session',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  cheating: {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'cheating',
      },
      {
        type: 'hide-entity',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'cheating',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'warning',
        issueType: 'cheating',
      },
    ],
  },
  disintermediation: {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'disintermediation',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'disintermediation',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'revoke-tutor-privilege',
      },
      {
        type: 'warning',
        issueType: 'disintermediation',
      },
    ],
  },
  'suspension-evasion': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: true,
      },
      {
        type: 'reporter-message',
        issueType: 'suspension-evasion',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'impersonation-or-misrepresentation': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'reporter-message',
        issueType: 'impersonation-or-misrepresentation',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'off-topic-conversation': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'off-topic-conversation',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'off-topic-conversation',
      },
    ],
    penaltyThreshold: {
      numStrikes: 3,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },
  'session-guidelines-violation': {
    baseActions: [
      {
        type: 'reporter-message',
        issueType: 'session-guidelines-violation',
      },
    ],
    strikeActions: [
      {
        type: 'warning',
        issueType: 'session-guidelines-violation',
      },
    ],
    penaltyThreshold: {
      numStrikes: 2,
    },
    penaltyActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
    ],
  },

  'harmful-generalizations': {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'reporter-message',
        issueType: 'harmful-generalizations',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  // Generic types

  'safety-or-wellbeing-concerns-other': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'dishonesty-other': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'crossing-privacy-boundaries-other': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'made-me-uncomfortable-other': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'content-violates-guidelines-other': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  benign: {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  duplicate: {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  other: {
    baseActions: [
      {
        type: 'escalation',
        shouldSuspend: false,
      },
      {
        type: 'reporter-message',
        issueType: 'other',
      },
    ],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },

  // Historical

  'not-let-into-session': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'circumventing-post-limits': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
  'classroom-inappropriate-behavior': {
    baseActions: [],
    strikeActions: [],
    penaltyThreshold: {
      numStrikes: 1,
    },
    penaltyActions: [],
  },
}

const GENERIC_SAFETY_SUSPENSION_EXPLANATION = composeRichText(ctx => [
  ctx.paragraph(
    'Your account has been temporarily suspended due to an urgent safety concern that was reported to our team. We are currently investigating the situation.'
  ),
  ctx.paragraph(''),
  ctx.paragraph(
    'If you believe this suspension was made in error, you can reach out to our Safety team through User Support to appeal. Please include any relevant context or information that may help us with our review.'
  ),
])

export const DETECTED_ISSUE_TYPE_TO_SUSPENSION_EXPLANATION: Record<
  DetectedIssueTypeWithSuspensionExplanation,
  SerializedRichText
> = {
  'suspicious-interactions-with-minor': GENERIC_SAFETY_SUSPENSION_EXPLANATION,
  'severely-crude-or-inappropriate-behavior': GENERIC_SAFETY_SUSPENSION_EXPLANATION,
  'threatening-violence': GENERIC_SAFETY_SUSPENSION_EXPLANATION,
  'slurs-and-hate-speech': GENERIC_SAFETY_SUSPENSION_EXPLANATION,
  'overly-personal-content': GENERIC_SAFETY_SUSPENSION_EXPLANATION,
  'suspension-evasion': composeRichText(ctx => [
    ctx.paragraph(
      'Your account has been suspended because we detected activity suggesting an attempt to evade a previous suspension. Creating new accounts or using other methods to bypass a suspension is a violation of our platform guidelines.'
    ),
    ctx.paragraph(
      'If you believe this suspension was made in error, you can reach out to our Safety team through User Support to appeal. Please include any relevant context or information that may help us with our review.'
    ),
  ]),
  'tutor-removed-learner': composeRichText(ctx => [
    ctx.paragraph(
      "Your account has been temporarily suspended because you were removed from a session by the tutor. When this happens, our system automatically pauses the learner's access so the Safety team can review what occurred in the session."
    ),
    ctx.paragraph(
      'If you believe this suspension was made in error, you can reach out to our Safety team through User Support to appeal. Please include any relevant context or information that may help us with our review.'
    ),
  ]),
  'circumventing-safety-features': composeRichText(ctx => [
    ctx.paragraph(
      "Your account has been temporarily suspended because we detected an attempt to bypass Schoolhouse's platform safety features. This is not the first time we have warned you about this behavior and as a result, your account has been suspended."
    ),
    ctx.paragraph(
      'If you believe this suspension was made in error, you can reach out to our Safety team through User Support to appeal. Please include any relevant context or information that may help us with our review.'
    ),
  ]),
  'vague-live-help-request': composeRichText(ctx => [
    ctx.paragraph(
      'Your account has been temporarily suspended because we detected repeated, vague posts in the Live Help system that appeared to be spam. Live Help is designed for specific academic questions, and posting too frequently without clear details disrupts the space for other learners and violates our platform guidelines.'
    ),
    ctx.paragraph(
      'If you believe this suspension was made in error, you can reach out to our Safety team through User Support to appeal. Please include any relevant context or information that may help us with our review.'
    ),
  ]),
  'user-under-13-self-report': composeRichText(ctx => [
    ctx.paragraph(
      "Your account has been temporarily suspended because the age you entered on your Schoolhouse profile indicates that you are under 13. Since all users must be at least 13 years old to use Schoolhouse, our system automatically pauses access when a user's account shows an age below that requirement."
    ),
    ctx.paragraph(
      "If the age on your account is incorrect, you're welcome to reach out and share your correct date of birth so we can review it. Otherwise, your account will need to remain suspended until you meet the age requirement."
    ),
  ]),
  'background-check-failed': composeRichText(ctx => [
    ctx.paragraph(
      "We're reaching out to let you know that your account has been suspended because information returned on your background check prevents us from approving you as a tutor on Schoolhouse. Since all tutors must meet our safety requirements, we're not able to allow tutoring access in this situation."
    ),
    ctx.paragraph(
      'If you believe there is additional information or context we should be aware of, you are welcome to reach out to us directly. Otherwise, your account will remain suspended.'
    ),
  ]),
}
